import DeleteDialog from 'shell/components/shared/delete-dialog';
import { Fragment, useState, useCallback } from 'react';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import TableCard from 'civil-status/components/stamp-certificate/table';
import { StampCertificateListCrumbs } from 'civil-status/constants/breadcrumb';
import ListHeader from 'shell/components/shared/list-header';
import { generatePath, useNavigate } from 'react-router-dom';
import { StampCertificateItemVM } from 'civil-status/view-models/stamp-certificate-list';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { StampCertificate } from 'shell/models/stamp-certificate';
import { HttpResponseList } from 'shell/models/http-response';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __CIVIL_STATUS_API_URL__ } from 'shell/constants/environment';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import { authsRowStampCertificate } from 'civil-status/components/stamp-certificate/table/table-row';

const StampCertificateList = (): React.ReactElement => {
  const [elementToDelete, setElementToDelete] = useState<null | StampCertificateItemVM>(null);
  const navigate = useNavigate();

  const [stampCertificate, getStampCertificate] = useHttpFetch<
    HttpResponseList<StampCertificate>,
    Array<StampCertificateItemVM>
  >(`${__CIVIL_STATUS_API_URL__}/testo-stampa-certificazione`, {
    onBootstrap: true,
    options: {
      method: 'GET',
    },
    transformer: (response) =>
      (response?.data?.payload?.list || []).map((item) => ({
        code: item.key?.codiceTesto || '',
        title: item.tipoElemento || '',
        text: item.testoStampa || '',
        type: item.tipoStampa || '',
        typeAct: item.tipoAtto || '',
        editor: item.codiceModificatore || '',
      })),
  });

  const stampCertificateData = (stampCertificate.data as Array<StampCertificateItemVM>) || [];

  const deleteItem = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}/testo-stampa-certificazione/cancella-testo-stampa-certificazione`,
    {
      options: {
        method: 'DELETE',
      },
    }
  );

  const clearToDelete = useCallback(() => setElementToDelete(null), [setElementToDelete]);

  const onConfirm = useCallback((): void => {
    if (elementToDelete) {
      (deleteItem({ url: `/${elementToDelete.code}` }) as ResponsePromise<void>).then(() => {
        getStampCertificate();
        setElementToDelete(null);
      });
    }
  }, [elementToDelete, deleteItem, getStampCertificate, setElementToDelete]);

  const auths = useActionAuthorization(authsRowStampCertificate);

  return (
    <Fragment>
      <Breadcrumbs items={StampCertificateListCrumbs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi nuovo timbro"
        addBtnLabel="Aggiungi nuovo timbro"
        canAdd={auths.add}
        onAddBtnClick={() => {
          navigate('/stato-civile/timbri/nuovo');
        }}
        title="Lista Timbri Certificato"
      />
      <div className="pmi-mt-2">
        <TableCard
          items={stampCertificateData}
          onTrashPress={(card) => setElementToDelete(card)}
          onEditPress={(item) =>
            navigate(generatePath(`/stato-civile/timbri/modifica/:id`, { id: item.code }))
          }
          onVisibilityPress={(item) =>
            navigate(generatePath(`/stato-civile/timbri/dettaglio/:id`, { id: item.code }))
          }
        />
      </div>
      {Boolean(elementToDelete) ? (
        <DeleteDialog
          isOpen={Boolean(elementToDelete)}
          onAbort={clearToDelete}
          onClose={clearToDelete}
          onConfirm={onConfirm}
        />
      ) : null}
    </Fragment>
  );
};

StampCertificateList.displayName = 'Stamp certificate list page component';

export default StampCertificateList;
